import { FC } from 'react'
import { useMutation } from '@apollo/client'
import Form from 'react-bootstrap/Form'
import { Formik, Field, FieldProps, ErrorMessage } from 'formik'
import { useHistory } from 'react-router-dom'
import * as Yup from 'yup'
import { phoneRegExp } from 'share/utils/utils'
import { Button } from 'react-bootstrap'

import { useAppDispatch } from 'hooks/store'
import useLoadingDelay from 'hooks/useLoadingDelay'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import {
  setForgotPasswordData,
  getEmptyForgotPasswordData
} from 'reducers/forgotPassword/forgotpasswordSlice'
import {
  RESET_CREDENTIALS,
  ResetCredentialsVariables,
  ResetCredentialsResponse
} from 'share/mutations/account'
import { PhoneInput } from 'components/molecules/PhoneInput'

import './styles.scss'

interface ForgotFormValues extends ResetCredentialsVariables {
  forgotPassword: boolean
  forgotUser: boolean
  checkValidation?: string | null
}

const ForgotSchema = Yup.object()
  .shape({
    lastName: Yup.string().required('Last Name is required'),
    mobilePhone: Yup.string().matches(
      phoneRegExp,
      'Mobile phone number is not valid'
    ),
    forgotUser: Yup.boolean(),
    forgotPassword: Yup.boolean()
  })
  .test('validateForgotChecks', '', (obj) => {
    if (obj.forgotPassword || obj.forgotUser) {
      return true
    }

    return new Yup.ValidationError(
      'Please check at least one',
      null,
      'checkValidation'
    )
  })

const initialValues: ForgotFormValues = {
  forgotPassword: false,
  forgotUser: false,
  lastName: '',
  mobilePhone: ''
}

interface ForgotFormProps {
  error: string
  setError: (err: string) => void
}

const ForgotForm: FC<ForgotFormProps> = ({ error, setError }) => {
  const dispatch = useAppDispatch()
  const history = useHistory()
  const [doForgot, { loading }] =
    useMutation<ResetCredentialsResponse, ResetCredentialsVariables>(
      RESET_CREDENTIALS
    )

  const loadingDelay = useLoadingDelay(loading)

  return (
    <>
      {loadingDelay && loadingSpinnerPortal()}
      <Formik
        initialValues={initialValues}
        validationSchema={ForgotSchema}
        onSubmit={(values) => {
          doForgot({
            variables: values
          })
            .then(({ data, errors }) => {
              if (data && !errors && !error) {
                const {
                  accountId,
                  maskedEmail,
                  message,
                  noAccountFlag,
                  hasNotCompletedAccountSetupSteps,
                  success
                } = data?.authentication?.resetCredentials

                if (success) {
                  if (!noAccountFlag && !hasNotCompletedAccountSetupSteps) {
                    const forgotPasswordData = getEmptyForgotPasswordData()

                    forgotPasswordData.accountId = accountId
                    forgotPasswordData.message = message
                    forgotPasswordData.maskedEmail = maskedEmail
                    forgotPasswordData.forgotUserIdChecked = values.forgotUser
                    forgotPasswordData.forgotPasswordChecked =
                      values.forgotPassword

                    dispatch(setForgotPasswordData(forgotPasswordData))

                    history.push({
                      pathname: '/login/forgot/validate',
                      state: values
                    })
                    return
                  }

                  if (hasNotCompletedAccountSetupSteps) {
                    history.push('/login/forgot/account-not-setup')
                  } else {
                    history.push('/login/forgot/noaccount')
                  }

                  return
                }

                setError('Error verifying your account please try again.')
              }
            })
            .catch((err) => {
              let networkMessage = ''
              if (err?.networkError?.result?.errors.length > 0) {
                networkMessage = err?.networkError?.result.errors[0].message
              }

              console.log('forgot password networkMessage ', networkMessage)
              console.log('forgot password err ', { err })
              setError(networkMessage || err?.message)
            })
        }}
      >
        {({ errors, touched, handleSubmit }) => {
          return (
            <Form onSubmit={handleSubmit} className='ui-forgot-form'>
              <Field name='lastName'>
                {({ field }: FieldProps) => (
                  <Form.Group controlId='lastName'>
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      type='text'
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      isInvalid={!!errors.lastName && touched.lastName}
                    />
                    <Form.Control.Feedback type='invalid'>
                      {errors.lastName}
                    </Form.Control.Feedback>
                  </Form.Group>
                )}
              </Field>

              <Form.Label>Mobile Phone (10 digits)</Form.Label>
              <Field
                type='text'
                id='mobilePhone'
                name='mobilePhone'
                as={PhoneInput}
                data-testid='forgot-mobile-phone-field'
                placeholder=''
                className={
                  errors.mobilePhone
                    ? 'form-control is-invalid'
                    : 'form-control'
                }
              />
              <ErrorMessage
                name='mobilePhone'
                render={(msg) => (
                  <div
                    className='text-danger'
                    data-testid='forgot-mobile-phone-field-error'
                  >
                    {msg}
                  </div>
                )}
              />

              <Field name='forgotUser'>
                {({ field }: FieldProps) => (
                  <Form.Group
                    controlId='forgotUser'
                    className='forgotUserCheck'
                  >
                    <Form.Check
                      type='checkbox'
                      label='Check if you forgot email address (User ID)'
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      isInvalid={!!errors.checkValidation && touched.forgotUser}
                    />
                  </Form.Group>
                )}
              </Field>

              <Field name='forgotPassword'>
                {({ field }: FieldProps) => (
                  <Form.Group controlId='forgotPassword'>
                    <Form.Check
                      type='checkbox'
                      label='Check if you forgot password'
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      isInvalid={!!errors.checkValidation && touched.forgotUser}
                      feedback={errors.checkValidation}
                    />
                  </Form.Group>
                )}
              </Field>
              <div className='ui-forgot-buttons'>
                <Button
                  disabled={loading}
                  type='submit'
                  variant='primary'
                  data-testid='forgot-continue-button'
                >
                  Continue
                </Button>
              </div>
            </Form>
          )
        }}
      </Formik>
    </>
  )
}

export default ForgotForm
